/**
 * TipTap / ProseMirror → plain text and rich inline runs (bold / italic / underline).
 * Shared by Node (pdfkit) and the React renderer — no zip / Node-only deps.
 */

export type RichTextMarks = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /** Story-critical proper noun — do not translate. */
  properNoun?: boolean;
  properNounTermId?: string;
};

export type RichTextRun = RichTextMarks & {
  text: string;
};

export type RichParagraph = {
  runs: RichTextRun[];
  plain: string;
};

function tipTapInlineText(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const record = value as Record<string, unknown>;
  if (record.type === "text" && typeof record.text === "string") return record.text;
  if (record.type === "hardBreak") return "\n";
  const children = Array.isArray(record.content) ? record.content : [];
  return children.map((child) => tipTapInlineText(child)).join("");
}

/** Walk any TipTap JSON node tree and return readable plain text (paragraphs joined with blank lines under `doc`). */
export function tipTapJsonToPlainText(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const record = value as Record<string, unknown>;
  const type = typeof record.type === "string" ? record.type : "";
  const children = Array.isArray(record.content) ? record.content : [];

  if (type === "doc") {
    return children
      .map((child) => tipTapJsonToPlainText(child))
      .map((line) => line.trim())
      .filter(Boolean)
      .join("\n\n");
  }

  if (type === "paragraph" || type === "heading") {
    return tipTapInlineText(record).trim();
  }

  if (type === "bulletList" || type === "orderedList") {
    return children
      .map((child) => tipTapJsonToPlainText(child))
      .map((line) => line.trim())
      .filter(Boolean)
      .join("\n");
  }

  if (type === "listItem") {
    return children
      .map((child) => tipTapJsonToPlainText(child))
      .map((line) => line.trim())
      .filter(Boolean)
      .join(" ");
  }

  if (children.length > 0 && (type === "blockquote" || type === "codeBlock")) {
    return children
      .map((child) => tipTapJsonToPlainText(child))
      .map((line) => line.trim())
      .filter(Boolean)
      .join("\n\n");
  }

  return tipTapInlineText(record).trim();
}

function marksFromTipTap(marks: unknown): RichTextMarks {
  const out: RichTextMarks = {};
  if (!Array.isArray(marks)) return out;
  for (const mark of marks) {
    if (!mark || typeof mark !== "object") continue;
    const type = String((mark as { type?: unknown }).type || "");
    if (type === "bold" || type === "strong") out.bold = true;
    if (type === "italic" || type === "em") out.italic = true;
    if (type === "underline") out.underline = true;
    if (type === "properNoun") {
      out.properNoun = true;
      const attrs = (mark as { attrs?: { termId?: unknown } }).attrs;
      if (typeof attrs?.termId === "string" && attrs.termId.trim()) {
        out.properNounTermId = attrs.termId.trim();
      }
    }
  }
  return out;
}

function sameMarks(a: RichTextMarks, b: RichTextMarks): boolean {
  return (
    Boolean(a.bold) === Boolean(b.bold) &&
    Boolean(a.italic) === Boolean(b.italic) &&
    Boolean(a.underline) === Boolean(b.underline) &&
    Boolean(a.properNoun) === Boolean(b.properNoun) &&
    (a.properNounTermId || "") === (b.properNounTermId || "")
  );
}

function pushRun(runs: RichTextRun[], text: string, marks: RichTextMarks): void {
  if (!text) return;
  const last = runs[runs.length - 1];
  if (last && sameMarks(last, marks)) {
    last.text += text;
    return;
  }
  runs.push({ text, ...marks });
}

function collectInlineRuns(node: unknown, inherited: RichTextMarks, out: RichTextRun[]): void {
  if (!node || typeof node !== "object") return;
  const record = node as Record<string, unknown>;
  const type = typeof record.type === "string" ? record.type : "";

  if (type === "text" && typeof record.text === "string") {
    const marks = { ...inherited, ...marksFromTipTap(record.marks) };
    pushRun(out, record.text.replace(/\u00A0/g, " "), marks);
    return;
  }
  if (type === "hardBreak") {
    pushRun(out, "\n", inherited);
    return;
  }

  const nextInherited =
    type === "bold" || type === "strong"
      ? { ...inherited, bold: true }
      : type === "italic" || type === "em"
        ? { ...inherited, italic: true }
        : type === "underline"
          ? { ...inherited, underline: true }
          : inherited;

  const children = Array.isArray(record.content) ? record.content : [];
  for (const child of children) collectInlineRuns(child, nextInherited, out);
}

function paragraphFromNode(node: unknown): RichParagraph | null {
  const runs: RichTextRun[] = [];
  collectInlineRuns(node, {}, runs);
  const plain = runs
    .map((r) => r.text)
    .join("")
    .replace(/\s*\n\s*/g, "\n")
    .trim();
  if (!plain) return null;
  // Trim leading/trailing whitespace runs without dropping marks mid-paragraph.
  while (runs.length && !runs[0].text.trim()) runs.shift();
  while (runs.length && !runs[runs.length - 1].text.trim()) runs.pop();
  if (runs.length) {
    runs[0].text = runs[0].text.replace(/^\s+/, "");
    runs[runs.length - 1].text = runs[runs.length - 1].text.replace(/\s+$/, "");
  }
  return { runs: runs.filter((r) => r.text.length > 0), plain };
}

/** Block-level rich paragraphs from a TipTap doc (preserves bold / italic / underline). */
export function tipTapJsonToRichParagraphs(value: unknown): RichParagraph[] {
  if (!value || typeof value !== "object") return [];
  const record = value as Record<string, unknown>;
  const type = typeof record.type === "string" ? record.type : "";
  const children = Array.isArray(record.content) ? record.content : [];

  if (type === "doc") {
    const out: RichParagraph[] = [];
    for (const child of children) {
      out.push(...tipTapJsonToRichParagraphs(child));
    }
    return out;
  }

  if (type === "paragraph" || type === "heading" || type === "blockquote" || type === "codeBlock") {
    const para = paragraphFromNode(record);
    return para ? [para] : [];
  }

  if (type === "bulletList" || type === "orderedList") {
    const out: RichParagraph[] = [];
    for (const child of children) out.push(...tipTapJsonToRichParagraphs(child));
    return out;
  }

  if (type === "listItem") {
    const para = paragraphFromNode(record);
    return para ? [para] : [];
  }

  const para = paragraphFromNode(record);
  return para ? [para] : [];
}

function asTipTapDoc(parsed: unknown): Record<string, unknown> | null {
  if (!parsed || typeof parsed !== "object") return null;
  if (Array.isArray(parsed)) {
    return { type: "doc", content: parsed };
  }
  const rec = parsed as Record<string, unknown>;
  if (rec.type === "doc") return rec;
  if (Array.isArray(rec.content) && (rec.type == null || rec.type === "doc")) {
    return { type: "doc", content: rec.content };
  }
  return null;
}

/**
 * If `value` is a JSON string for a TipTap doc (or a double-encoded one), return the
 * parsed `{ type: "doc", ... }` object; otherwise `null`.
 */
export function tryParseStringifiedTipTapDoc(value: string): Record<string, unknown> | null {
  let trimmed = String(value ?? "")
    .replace(/^\uFEFF/, "")
    .trim();
  if (!trimmed) return null;

  for (let depth = 0; depth < 3; depth++) {
    if (!(trimmed.startsWith('"') && trimmed.endsWith('"'))) break;
    try {
      const once = JSON.parse(trimmed) as unknown;
      if (typeof once !== "string") break;
      trimmed = once.replace(/^\uFEFF/, "").trim();
    } catch {
      break;
    }
  }

  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;

  try {
    return asTipTapDoc(JSON.parse(trimmed));
  } catch {
    return null;
  }
}

/** True when a string looks like TipTap JSON that must never be treated as prose. */
export function looksLikeTipTapJsonString(value: string): boolean {
  const t = value.replace(/^\uFEFF/, "").trim();
  if (!t.startsWith("{") && !t.startsWith("[") && !t.startsWith('"')) return false;
  return (
    /"type"\s*:\s*"doc"/.test(t) ||
    /"type"\s*:\s*"paragraph"/.test(t) ||
    /\\"type\\"\s*:\s*\\"doc\\"/.test(t) ||
    /\\"type\\"\s*:\s*\\"paragraph\\"/.test(t)
  );
}

/** Split plain text into rich paragraphs (no marks) — fallback for non-TipTap content. */
export function plainTextToRichParagraphs(text: string): RichParagraph[] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\u00A0/g, " ").trim();
  if (!normalized) return [];
  return normalized
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((plain) => ({ plain, runs: [{ text: plain.replace(/\n/g, " ") }] }));
}

/** Build a TipTap doc from rich paragraphs (bold / italic / underline runs). */
export function richParagraphsToTipTapDoc(paragraphs: RichParagraph[]): {
  type: "doc";
  content: Array<{
    type: "paragraph";
    attrs: { id: string };
    content: Array<{ type: "text"; text: string; marks?: Array<{ type: string }> }>;
  }>;
} {
  const content = paragraphs.map((para) => {
    const runs = para.runs.length
      ? para.runs
      : para.plain
        ? [{ text: para.plain }]
        : [];
    const inline = runs
      .filter((run) => run.text.length > 0)
      .map((run) => {
        const marks: Array<{ type: string; attrs?: Record<string, unknown> }> = [];
        if (run.bold) marks.push({ type: "bold" });
        if (run.italic) marks.push({ type: "italic" });
        if (run.underline) marks.push({ type: "underline" });
        if (run.properNoun) {
          marks.push(
            run.properNounTermId
              ? { type: "properNoun", attrs: { termId: run.properNounTermId } }
              : { type: "properNoun" }
          );
        }
        return marks.length
          ? { type: "text" as const, text: run.text, marks }
          : { type: "text" as const, text: run.text };
      });
    return {
      type: "paragraph" as const,
      attrs: { id: "" },
      content: inline.length ? inline : [],
    };
  });
  return {
    type: "doc",
    content: content.length ? content : [{ type: "paragraph", attrs: { id: "" }, content: [] }],
  };
}

function splitWords(text: string): string[] {
  // Keep letters/digits together; separate punctuation so "always." aligns with "always".
  return text.match(/[A-Za-z0-9’']+|[^\sA-Za-z0-9’']+|\s+/g) ?? [];
}

function marksForBaseWord(baseRuns: RichTextRun[], wordStart: number): RichTextMarks {
  let offset = 0;
  for (const run of baseRuns) {
    const next = offset + run.text.length;
    if (wordStart >= offset && wordStart < next) {
      return {
        bold: run.bold,
        italic: run.italic,
        underline: run.underline,
        properNoun: run.properNoun,
        properNounTermId: run.properNounTermId,
      };
    }
    offset = next;
  }
  return {};
}

/** Copy B/I/U from base runs onto rewrite plain text via word-level LCS alignment. */
export function transferRunsOntoPlain(baseRuns: RichTextRun[], rewritePlain: string): RichTextRun[] {
  const normalized = rewritePlain.replace(/\s+/g, " ").trim();
  if (!normalized) return [];
  if (!baseRuns.length) return [{ text: normalized }];

  const basePlain = baseRuns.map((r) => r.text).join("");
  const baseWords = splitWords(basePlain);
  const rewriteWords = splitWords(normalized);

  const rows = baseWords.length + 1;
  const cols = rewriteWords.length + 1;
  const table = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      if (baseWords[i - 1] === rewriteWords[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1;
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
      }
    }
  }

  const matchedRewrite = new Map<number, number>();
  let i = baseWords.length;
  let j = rewriteWords.length;
  while (i > 0 && j > 0) {
    if (baseWords[i - 1] === rewriteWords[j - 1]) {
      matchedRewrite.set(j - 1, i - 1);
      i -= 1;
      j -= 1;
    } else if (table[i - 1][j] >= table[i][j - 1]) {
      i -= 1;
    } else {
      j -= 1;
    }
  }

  let baseCharOffset = 0;
  const baseWordStarts: number[] = [];
  for (const word of baseWords) {
    baseWordStarts.push(baseCharOffset);
    baseCharOffset += word.length;
  }

  const out: RichTextRun[] = [];
  for (let wi = 0; wi < rewriteWords.length; wi += 1) {
    const word = rewriteWords[wi];
    const baseWordIndex = matchedRewrite.get(wi);
    const marks =
      baseWordIndex != null ? marksForBaseWord(baseRuns, baseWordStarts[baseWordIndex] ?? 0) : {};
    pushRun(out, word, marks);
  }
  return out.length ? out : [{ text: normalized }];
}

type ParaAlignOp =
  | { type: "equal"; base: RichParagraph; rewrite: RichParagraph }
  | { type: "changed"; base: RichParagraph; rewrite: RichParagraph }
  | { type: "added"; rewrite: RichParagraph }
  | { type: "removed"; base: RichParagraph };

function alignRichParagraphs(base: RichParagraph[], rewrite: RichParagraph[]): ParaAlignOp[] {
  const baseTexts = base.map((p) => p.plain.replace(/\s+/g, " ").trim());
  const rewriteTexts = rewrite.map((p) => p.plain.replace(/\s+/g, " ").trim());
  const rows = baseTexts.length + 1;
  const cols = rewriteTexts.length + 1;
  const table = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      if (baseTexts[i - 1] === rewriteTexts[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1;
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
      }
    }
  }

  const ops: ParaAlignOp[] = [];
  let i = baseTexts.length;
  let j = rewriteTexts.length;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && baseTexts[i - 1] === rewriteTexts[j - 1]) {
      ops.push({ type: "equal", base: base[i - 1], rewrite: rewrite[j - 1] });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || table[i][j - 1] >= table[i - 1][j])) {
      ops.push({ type: "added", rewrite: rewrite[j - 1] });
      j -= 1;
    } else if (i > 0) {
      ops.push({ type: "removed", base: base[i - 1] });
      i -= 1;
    } else {
      break;
    }
  }
  ops.reverse();

  // Collapse adjacent removed+added into changed
  const collapsed: ParaAlignOp[] = [];
  for (let index = 0; index < ops.length; index += 1) {
    const op = ops[index];
    const next = ops[index + 1];
    if (op.type === "removed" && next?.type === "added") {
      collapsed.push({ type: "changed", base: op.base, rewrite: next.rewrite });
      index += 1;
      continue;
    }
    if (op.type === "added" && next?.type === "removed") {
      collapsed.push({ type: "changed", base: next.base, rewrite: op.rewrite });
      index += 1;
      continue;
    }
    collapsed.push(op);
  }
  return collapsed;
}

/**
 * Align base rich paragraphs with a rewrite (plain or rich) and preserve B/I/U
 * on unchanged text; transfer marks onto rewritten paragraphs via word LCS.
 */
export function transferFormattingOntoRewrite(
  baseParagraphs: RichParagraph[],
  rewriteParagraphs: RichParagraph[]
): RichParagraph[] {
  if (!rewriteParagraphs.length) return [];
  if (!baseParagraphs.length) return rewriteParagraphs;

  const ops = alignRichParagraphs(baseParagraphs, rewriteParagraphs);
  const out: RichParagraph[] = [];
  for (const op of ops) {
    if (op.type === "equal") {
      out.push(op.base);
      continue;
    }
    if (op.type === "removed") continue;
    if (op.type === "added") {
      out.push(op.rewrite);
      continue;
    }
    const runs = transferRunsOntoPlain(op.base.runs, op.rewrite.plain);
    out.push({
      plain: op.rewrite.plain,
      runs,
    });
  }
  return out;
}

/** Prefer exact plain-text matches from primary, then secondary sources for marks. */
export function hydrateMarksFromSources(
  plainParagraphs: RichParagraph[],
  primary: RichParagraph[],
  secondary: RichParagraph[]
): RichParagraph[] {
  const primaryByPlain = new Map(
    primary.map((p) => [p.plain.replace(/\s+/g, " ").trim(), p] as const)
  );
  const secondaryByPlain = new Map(
    secondary.map((p) => [p.plain.replace(/\s+/g, " ").trim(), p] as const)
  );
  return plainParagraphs.map((para) => {
    const key = para.plain.replace(/\s+/g, " ").trim();
    const fromPrimary = primaryByPlain.get(key);
    if (fromPrimary) return fromPrimary;
    const fromSecondary = secondaryByPlain.get(key);
    if (fromSecondary) return fromSecondary;
    // Soft transfer: try best secondary/primary paragraph by word overlap via transfer
    const donor = secondary[0] ? secondary : primary;
    if (donor.length === 1 && plainParagraphs.length === 1) {
      return { plain: para.plain, runs: transferRunsOntoPlain(donor[0].runs, para.plain) };
    }
    return para;
  });
}
